import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getFirestore, collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { app } from "../firebase";
import { ETHNICITY_LABELS, SEX_LABELS } from "../shared/data/client-labels";

/**
 * View clients for a specific organization
 */
export default function ViewClients({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState(null);
  const [clients, setClients] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchOrganizationAndClients() {
      try {
        const db = getFirestore(app);
        
        // Fetch organization
        const orgDoc = await getDoc(doc(db, "organizations", id));
        if (!orgDoc.exists()) {
          setError("Organization not found");
          return;
        }
        const orgData = { id: orgDoc.id, ...orgDoc.data() };
        setOrganization(orgData);

        // Fetch clients
        const clientsRef = collection(db, "clients");
        const q = query(clientsRef, where("organization_id", "==", id));
        const querySnapshot = await getDocs(q);
        
        const clientsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setClients(clientsData);
      } catch (e) {
        console.error("Error fetching data:", e);
        setError("Failed to load data");
      }
    }

    fetchOrganizationAndClients();
  }, [id]);

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
        <button
          onClick={() => navigate("/organizations")}
          className="mt-4 text-sm text-indigo-600 hover:text-indigo-900"
        >
          ← Back to Organizations
        </button>
      </div>
    );
  }

  if (!organization || !clients) {
    return <p>Loading...</p>;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <div className="flex items-center gap-x-3">
            <button
              onClick={() => navigate("/organizations")}
              className="text-sm text-indigo-600 hover:text-indigo-900"
            >
              ← Back to Organizations
            </button>
            <h1 className="text-base font-semibold leading-6 text-gray-900">
              Clients - {organization.name}
            </h1>
          </div>
          <p className="mt-2 text-sm text-gray-700">
            A list of all clients in this organization.
          </p>
        </div>
      </div>
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <table className="min-w-full divide-y divide-gray-300">
              <thead>
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                    Name
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Date of Birth
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Sex
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Ethnicity
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Hispanic/Latino
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {clients.map((client) => (
                  <tr key={client.id}>
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
                      {client.Name}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {new Date(client.DateOfBirth).toLocaleDateString()}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {SEX_LABELS[client.Sex]}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {ETHNICITY_LABELS[client.Ethnicity]}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
                        client.HispanicLatino
                          ? 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-700/10'
                          : 'bg-gray-50 text-gray-700 ring-1 ring-inset ring-gray-700/10'
                      }`}>
                        {client.HispanicLatino ? "Yes" : "No"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
} 